/**
 * 线上协议的**纯类型与常量**。前后端共用。
 *
 * Zod schema 在 `@avalon/shared/schemas`，**只有服务端会 import** ——
 * 浏览器不需要校验入站 payload（那是服务端的职责），
 * 把 zod 打进前端包白白多 265 KB。
 */

import type { GameEvent, GameSettings } from "./game.js";
import type { ClientGameView } from "./view.js";

// ──────────────────────────── 文本清洗 ────────────────────────────

/**
 * 不可见字符：控制字符、零宽空格/连接符、双向文本覆盖符、字形连接符、BOM。
 * 这些是「看起来是空昵称」「和别人同名」「把后面的文字反转」的常见手法，直接剔掉。
 * 用显式 \uXXXX 转义写，不用字面量 —— 字面量在编辑器和 diff 里会被悄悄吃掉。
 */
const INVISIBLE =
  /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/g;

/** 昵称/房间名清洗。前端也调它做实时预览，保证两边结果一致 */
export const sanitizeText = (raw: string, maxLength: number): string =>
  raw.replace(INVISIBLE, "").replace(/\s+/g, " ").trim().slice(0, maxLength);

export const NICK_MAX = 12;
export const ROOM_NAME_MAX = 20;

/** 一条聊天最多这么长。手机上一屏能看完，也压住单条刷屏 */
export const CHAT_TEXT_MAX = 80;
/**
 * 每个频道留多少条历史。
 *
 * 聊天记录挂在对局状态上，而**每次状态变更都全量下发**（PLAN §5）——
 * 留太多就等于每个动作都重发一遍聊天室。50 条够回溯刚才说了什么，
 * 又不至于把每次推送撑大。
 */
export const CHAT_HISTORY_MAX = 50;

/**
 * 房间码：**6 位纯数字**。
 *
 * 线下面对面玩，房间码是喊出来的、在手机上敲进去的 ——
 * 数字念起来没有「B 还是 D」的歧义，输入时也能直接调出数字键盘。
 * 混合字母表原本还要专门剔掉形近的 0/O/1/I，纯数字连这个问题都不存在。
 */
export const ROOM_CODE_ALPHABET = "0123456789";
export const ROOM_CODE_LENGTH = 6;
export const ROOM_CODE_PATTERN = /^\d{6}$/;

// ──────────────────────────── 身份 ────────────────────────────

/**
 * 头像。用 DiceBear `micah`（与 vue-color-avatar 同源美术，CC BY 4.0）。
 * 只存 seed 和背景色几十个字节，SVG 由前端本地生成，零网络请求。
 */
export interface Avatar {
  readonly seed: string;
  /** 背景色，六位十六进制不带 # */
  readonly bg: string;
}

export interface Profile {
  readonly nick: string;
  readonly avatar: Avatar;
}

export interface RoomOptions {
  readonly name: string;
  readonly visibility: "PUBLIC" | "PRIVATE";
  readonly allowSpectators: boolean;
}

// ──────────────────────────── 客户端 → 服务端 ────────────────────────────

/**
 * 对局动作。对照 `@avalon/engine` 的 `Action`，**故意不含 `seat`** ——
 * 座位由服务端按连接身份填入。让客户端自报座位，
 * 等于把「你是不是队长」「这是不是你的票」全部校验送出去。
 * `ADVANCE` 同理不含 `byHost`：是不是房主由服务端认定。
 */
export type ClientAction =
  | { readonly type: "ACK_ROLE" }
  | {
      readonly type: "PROPOSE_TEAM";
      readonly team: readonly number[];
      readonly speakDirection: "CW" | "CCW" | null;
    }
  | { readonly type: "VOTE"; readonly approve: boolean }
  | { readonly type: "PLAY_CARD"; readonly success: boolean }
  | { readonly type: "LADY_CHECK"; readonly targetSeat: number }
  | { readonly type: "EARLY_ASSASSINATE" }
  | { readonly type: "ASSASSINATE"; readonly targetSeat: number }
  | { readonly type: "ADVANCE" };

/** 事件名列表。schema 表在 @avalon/shared/schemas，两边必须对齐 */
export const CLIENT_EVENT_NAMES = [
  "room:join",
  "room:leave",
  "room:profile",
  "room:sit",
  "room:stand",
  "room:ready",
  "room:seatCount",
  "room:dissolve",
  "room:reorder",
  "room:shuffleSeats",
  "room:requestSwap",
  "room:respondSwap",
  "room:settings",
  "room:options",
  "room:kick",
  "room:transferHost",
  "game:start",
  "game:abort",
  "game:restart",
  "game:action",
  /** 献花 / 砸鸡蛋。纯玩梗，不影响任何规则 */
  "game:react",
  /** 聊天。两个频道：所有人 / 互认的坏人 */
  "game:chat",
  "game:emote",
  /** 测延迟用的心跳。服务端原样回 net:pong，往返差就是 RTT */
  "net:ping",
] as const;

/** 能扔给别人的东西。**只是气氛，不进战报、不影响任何判定** */
/**
 * 朝别人扔的东西。**元数据放这里，前后端共用** ——
 * 加一种新的只改这一处，服务端校验和前端渲染同时跟上。
 */
export const REACTIONS = ["FLOWER", "EGG", "TOMATO", "WATER"] as const;
export type Reaction = (typeof REACTIONS)[number];

export interface ReactionMeta {
  readonly emoji: string;
  readonly label: string;
  /** 砸中那一刻炸开的东西 */
  readonly hit: string;
  /**
   * 落地表现。**送花和挨砸不能共用** ——
   * 送花却让对方头像抖一下，那是挨揍的语言。
   */
  readonly impact: "gift" | "hit" | "splash";
}

export const REACTION_META: { readonly [K in Reaction]: ReactionMeta } = {
  FLOWER: { emoji: "🌹", label: "献花", hit: "🌸", impact: "gift" },
  EGG: { emoji: "🥚", label: "砸蛋", hit: "💥", impact: "hit" },
  TOMATO: { emoji: "🍅", label: "扔番茄", hit: "🍅", impact: "hit" },
  WATER: { emoji: "🪣", label: "泼水", hit: "💦", impact: "splash" },
};

/** 连发一次扔几个 */
export const BURST_COUNT = 10;

/**
 * 表情包。点**自己**头像发，不针对任何人，全场都看得到。
 * 图在 assets/roles/emotes/（跟角色卡走同一条生成流水线）。
 */
export const EMOTES = [
  { id: "naked-rush", art: "naked-rush", text: "你们三狼裸冲啊" },
  { id: "trust-me", art: "trust-me", text: "我信你个鬼" },
  { id: "speak-up", art: "speak-up", text: "你倒是说话啊" },
  { id: "again-you", art: "again-you", text: "又是你？" },
  { id: "stop-acting", art: "stop-acting", text: "别演了，出来吧" },
  { id: "this-is-fine", art: "this-is-fine", text: "这波稳了" },
  { id: "why-me", art: "why-me", text: "刺客你看我干嘛" },
  { id: "im-out", art: "im-out", text: "下把别叫我了" },
] as const;

export type EmoteId = (typeof EMOTES)[number]["id"];
export const isEmoteId = (v: string): v is EmoteId => EMOTES.some((e) => e.id === v);

export type ClientEventName = (typeof CLIENT_EVENT_NAMES)[number];

// ──────────────────────────── 服务端 → 客户端 ────────────────────────────


export interface PublicPlayer {
  readonly id: string;
  readonly nick: string;
  readonly avatar: Avatar;
  /** null = 还没入座（开局前在等待区，开局后就是观战） */
  readonly seat: number | null;
  readonly connected: boolean;
  readonly isHost: boolean;
  /** 已准备。只有在座玩家有意义 */
  readonly ready: boolean;
}

/** 一次待处理的换座请求。线下常常临时挪位置，得让玩家自己能换 */
export interface PendingSwap {
  readonly fromPlayerId: string;
  readonly toPlayerId: string;
  readonly fromSeat: number;
  readonly toSeat: number;
}

export interface RoomView {
  readonly id: string;
  readonly name: string;
  readonly visibility: "PUBLIC" | "PRIVATE";
  readonly allowSpectators: boolean;
  readonly hostId: string;
  readonly settings: GameSettings;
  /** 房主设定的座位数，也就是「几人局」 */
  readonly seatCount: number;
  /**
   * 环形座位，索引即 seatIndex，长度恒为 seatCount。
   * `null` 表示空位 —— 玩家点空位入座。开局后不会有 null。
   */
  readonly seats: readonly (PublicPlayer | null)[];
  /** 没入座的人：开局前是等待区，开局后是观战席 */
  readonly standing: readonly PublicPlayer[];
  readonly inGame: boolean;
  /** 当前配置下能否开局；不能开时 `startBlockedReason` 说明原因 */
  readonly canStart: boolean;
  readonly startBlockedReason: string | null;
  /** 当前待处理的换座请求，同一时刻只允许有一个 */
  readonly pendingSwap: PendingSwap | null;
}

/** 大厅列表条目。刻意不含玩家身份、不含 IP */
export interface RoomSummary {
  readonly id: string;
  readonly name: string;
  readonly playerCount: number;
  readonly inGame: boolean;
  readonly allowSpectators: boolean;
}

/** 每次状态变更都下发全量（裁剪后）。状态才几 KB，简单胜过增量 diff，且天然解决重连一致性 */
export interface StatePayload {
  readonly room: RoomView;
  /** 未开局为 null */
  readonly game: ClientGameView | null;
  /** 我是谁。观战者也有，用来认自己 */
  readonly selfId: string;
}

export type ServerErrorCode =
  | "ROOM_NOT_FOUND"
  | "ROOM_FULL"
  | "ROOM_IN_GAME"
  | "NOT_HOST"
  | "NOT_SEATED"
  | "ALREADY_SEATED"
  | "SPECTATORS_DISABLED"
  | "INVALID_PAYLOAD"
  | "RATE_LIMITED"
  | "CANNOT_START"
  | "NOT_IN_GAME"
  | "SWAP_TARGET_BUSY"
  | "NO_PENDING_SWAP";

export interface ServerEvents {
  state: (payload: StatePayload) => void;
  /** 一次性提示，用来播动画和音效 */
  event: (payload: GameEvent) => void;
  error: (payload: { code: ServerErrorCode | string; message: string }) => void;
  "room:list": (payload: { rooms: readonly RoomSummary[] }) => void;
  /** 被踢或房间解散 */
  kicked: (payload: { reason: string }) => void;
  /** `net:ping` 的回声，`t` 是客户端发出时自己的时间戳 */
  "net:pong": (payload: { t: number }) => void;
  /**
   * 谁朝谁扔了什么。**全房间群发** —— 里面全是公开信息（座位号 + 花还是蛋），
   * 不含任何身份，所以不需要逐人裁剪。
   */
  emote: (payload: { fromSeat: number; emoteId: string }) => void;
  reaction: (payload: {
    fromSeat: number;
    targetSeat: number;
    kind: Reaction;
    /** 连发时一次扔几个 */
    count: number;
  }) => void;
}
